import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import i18next from 'i18next'
import { safeStorage } from './safeStorage'
import { useAuthStore } from './authStore'
import apiClient from '@/api/client'
import languagesConfig from '@languages'

interface UIState {
  // Sidebar state
  sidebarOpen: boolean
  sidebarCollapsed: boolean

  // Theme
  theme: 'light' | 'dark' | 'system'

  // Language — 'auto' or any supported language code from languages.json
  language: string

  // Selected items
  selectedCameraId: number | null
  selectedGroupId: number | null

  // Actions
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setTheme: (theme: 'light' | 'dark' | 'system') => void
  setLanguage: (language: string) => void
  setSelectedCamera: (id: number | null) => void
  setSelectedGroup: (id: number | null) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: typeof window !== 'undefined' && window.innerWidth >= 1024,
      sidebarCollapsed: false,
      theme: 'system',
      language: 'auto',
      selectedCameraId: null,
      selectedGroupId: null,

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setTheme: (theme) => {
        // Apply theme to document
        const root = window.document.documentElement
        root.classList.remove('light', 'dark')

        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
          root.classList.add(systemTheme)
        } else {
          root.classList.add(theme)
        }

        set({ theme })

        // Sync theme preference to backend if authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (isAuthenticated) {
          apiClient.patch('/user/profile', { theme }).catch(() => {})
        }
      },
      setLanguage: (language) => {
        let resolvedLang: string
        if (language === 'auto') {
          // Remove override — let i18next detect from browser
          localStorage.removeItem('i18nextLng')
          const browserLang = navigator.language.split('-')[0]
          resolvedLang = languagesConfig.supported.includes(browserLang)
            ? browserLang
            : languagesConfig.default
        } else {
          resolvedLang = language
        }
        i18next.changeLanguage(resolvedLang)
        document.documentElement.lang = resolvedLang
        set({ language })

        // Sync language preference to backend if authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (isAuthenticated) {
          apiClient.patch('/user/profile', { language: resolvedLang }).catch(() => {
            // Silent fail — language is persisted locally regardless
          })
        }
      },
      setSelectedCamera: (id) => set({ selectedCameraId: id }),
      setSelectedGroup: (id) => set({ selectedGroupId: id }),
    }),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        language: state.language,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply the persisted theme class to the document immediately on load
        if (state) state.setTheme(state.theme)
      },
    }
  )
)
